import { useState } from 'react';
import { array } from 'fp-ts';
import { PortfolioEnterpriseGroupResult } from '@mews-commander/data-access';
import { useDialogAction, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Divider, DropDownButton, DropdownItem, IconName } from '@optimus-web/core';
import { CreateRateContainer } from '../../../enterpriseRateManagement/components/sections/components/rateForm/CreateRateContainer';
import { CreateVoucherContainer } from '../../../enterpriseRateManagement/components/sections/components/vouchers/CreateVoucherContainer';
import { DeletePropertyGroupsDialog } from '../../PropertyGroups/DeletePropertyGroupsDialog';
import { useDeletePropertyGroupsMutation } from '../../queries';
import { PropertyGroupContainer } from '../../PropertyGroups/PropertyGroupContainer/PropertyGroupContainer';
import { FORM_TYPE } from '../../PropertyGroups/PropertyGroupDialog/constraints';
import {
    CREATE_RATE_GROUP_STARTING_POINT,
    CREATE_RATE_STARTING_POINT,
    CREATE_VOUCHER_STARTING_POINT,
} from '../../../enterpriseRateManagement/utilities/userFlowTestIds';
import { ADD_TO_PROPERTY_GROUP_STARTING_POINT } from '../../utilities/userFlowTestIds';
import { useCreateRateGroupDialogForm } from '../../../enterpriseRateManagement/components/sections/components/rateGroup/hooks';

interface RowActionsProps {
    propertyGroup?: PortfolioEnterpriseGroupResult;
    deleteTextKey?: Mews.TextKey;
    enterpriseIds: string[];
    onDelete?: (propertyGroupId: string) => void;
}

export const RowActions = ({ propertyGroup, deleteTextKey, onDelete, enterpriseIds }: RowActionsProps) => {
    const { states: voucherStates, handlers: voucherHandlers } = useDialogAction(async () => Promise.resolve());
    const { states: rateStates, handlers: rateHandlers } = useDialogAction(async () => Promise.resolve());
    const { states: propertyGroupStates, handlers: propertyGroupHandlers } = useDialogAction(async () =>
        Promise.resolve()
    );

    const [renderDialog, toggleDialog] = useCreateRateGroupDialogForm({
        enterpriseIds,
        propertyGroupsIds: propertyGroup?.id ? [propertyGroup?.id] : undefined,
    });

    const [initialPropertyGroupForm, setInitialPropertyGroupForm] = useState<FORM_TYPE>(FORM_TYPE.CREATE_NEW);

    const deletePropertyGroupsMutation = useDeletePropertyGroupsMutation();
    // eslint-disable-next-line @typescript-eslint/require-await
    const deleteAction = useDialogAction(async () => {
        if (propertyGroup) {
            deletePropertyGroupsMutation.mutate([propertyGroup], {
                onSuccess: () => onDelete?.(propertyGroup.id),
            });
        }
    });
    const [isDependentRate, setIsDependentRate] = useState(false);

    const showPropertyGroupOptions = !propertyGroup;
    const shouldDisableCreationOptions = propertyGroup ? array.isEmpty(propertyGroup.propertyIds) : false;

    return (
        <>
            <DropDownButton icon={IconName.More} variant="ghost" size="small">
                <DropdownItem
                    data-test-id={CREATE_RATE_STARTING_POINT}
                    label={<LocalizedText textKey={TextKey.CreateBaseRate} />}
                    disabled={shouldDisableCreationOptions}
                    onClick={() => {
                        setIsDependentRate(false);
                        rateHandlers.handleOpen();
                    }}
                />
                <DropdownItem
                    data-test-id={CREATE_RATE_STARTING_POINT}
                    label={<LocalizedText textKey={TextKey.CreateDependentRate} />}
                    disabled={shouldDisableCreationOptions}
                    onClick={() => {
                        setIsDependentRate(true);
                        rateHandlers.handleOpen();
                    }}
                />
                <DropdownItem
                    data-test-id={CREATE_VOUCHER_STARTING_POINT}
                    label={<LocalizedText textKey={TextKey.CreateVoucher} />}
                    disabled={shouldDisableCreationOptions}
                    onClick={voucherHandlers.handleOpen}
                />

                <DropdownItem
                    data-test-id={CREATE_RATE_GROUP_STARTING_POINT}
                    label={<LocalizedText textKey={TextKey.RateGroupCreate} />}
                    disabled={shouldDisableCreationOptions}
                    onClick={toggleDialog}
                />
                <Divider />

                {showPropertyGroupOptions ? (
                    <DropdownItem
                        data-test-id={ADD_TO_PROPERTY_GROUP_STARTING_POINT}
                        label={<LocalizedText textKey={TextKey.PropertyAddGroup} />}
                        onClick={() => {
                            setInitialPropertyGroupForm(FORM_TYPE.ADD_TO_EXISTING);
                            propertyGroupHandlers.handleOpen();
                        }}
                    />
                ) : null}
                {deleteTextKey && (
                    <DropdownItem
                        label={<LocalizedText textKey={deleteTextKey} />}
                        variant="danger"
                        onClick={deleteAction.handlers.handleOpen}
                    />
                )}
            </DropDownButton>
            {propertyGroup && deleteAction.states.isDialogOpened ? (
                <DeletePropertyGroupsDialog propertyGroups={[propertyGroup]} deleteAction={deleteAction} />
            ) : null}
            {voucherStates.isDialogOpened && (
                <CreateVoucherContainer
                    enterpriseIds={enterpriseIds}
                    isOpened={voucherStates.isDialogOpened}
                    onConfirm={voucherHandlers.handleConfirm}
                    onCancel={voucherHandlers.handleCancel}
                />
            )}
            {rateStates.isDialogOpened && (
                <CreateRateContainer
                    isOpened={rateStates.isDialogOpened}
                    onConfirm={rateHandlers.handleConfirm}
                    onCancel={rateHandlers.handleCancel}
                    isDependentRate={isDependentRate}
                    enterpriseIds={enterpriseIds}
                />
            )}
            {propertyGroupStates.isDialogOpened && (
                <PropertyGroupContainer
                    onConfirm={propertyGroupHandlers.handleConfirm}
                    onCancel={propertyGroupHandlers.handleCancel}
                    enterpriseIds={enterpriseIds}
                    isOpened={propertyGroupStates.isDialogOpened}
                    initialTabValue={initialPropertyGroupForm}
                />
            )}
            {renderDialog()}
        </>
    );
};
